import { css } from '@emotion/react';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { CompactTable, CompactTableRowActionsProps } from '../compact-table';
import { ProviderNameCell, ShowOnCalendarCell } from './ManagePractitionersV3Cells';
import { ManagePractitionersV3CommonTableProps } from './types';

type ManagePractitionersV3CompactTableProps = ManagePractitionersV3CommonTableProps & {
  tableRowsAction: CompactTableRowActionsProps<Provider>;
};

export const ManagePractitionersV3CompactTable = ({
  locationId,
  setPageConfig,
  tableRowsAction,
  debouncedSearchValue,
  pageConfig,
}: ManagePractitionersV3CompactTableProps) => {
  const { t } = useTranslation('scheduleAppointmentTypes');
  const { invalidateListProviders } = SchedulerV3.Helpers.useSchedulerV3InvalidateQueries();

  const providerInfiniteListQuery = SchedulerV3.Helpers.useGetPractitionerInfiniteListForV3({
    locationId: locationId || '',
    searchText: debouncedSearchValue,
    pageLimit: pageConfig.limit,
  });
  const { isLoading } = providerInfiniteListQuery;

  const handleTableRefetch = () => {
    invalidateListProviders(locationId);
  };

  return (
    <>
      <CompactTable
        tableId='manage-practitioners-compact-table'
        isLoading={isLoading}
        infiniteQueryProps={providerInfiniteListQuery}
        rowElement={(provider: Provider) => (
          <CompactTable.RowHeader
            css={styles.rowHeader(provider.isArchived)}
            title={<ProviderNameCell provider={provider} />}
          />
        )}
        expandedRowElement={(provider: Provider) => <ExpandedRow provider={provider} />}
        toolbarElement={<CompactTable.Toolbar />}
        rowActions={tableRowsAction}
        tableActions={[
          {
            Icon: () => <Icon name='update' />,
            label: t('Refetch'),
            onClick: handleTableRefetch,
          },
        ]}
        globalSearchConfig={{
          initialValue: '',
          placeholder: t('Search Provider Name'),
          searchHandler: (searchValue) => {
            const trimmedSearchValue = searchValue.trim();
            // search data if there is more than one character in the search field since backend doesn't support single character search
            const hasOneSearchChar = trimmedSearchValue.length === 1;
            if (!hasOneSearchChar) {
              setPageConfig((prev) => ({ ...prev, page: 1, searchValue: trimmedSearchValue }));
            }
          },
        }}
        styleConfig={{ toolbar: css({ marginTop: 0 }) }}
      />
    </>
  );
};

const ExpandedRow = ({ provider }: { provider: Provider }) => {
  const { t } = useTranslation('scheduleAppointmentTypes');

  return (
    <CompactTable.ExpandedRow>
      <CompactTable.InfoRow label={t('First Name')} value={provider.firstName || '-'} />
      <CompactTable.InfoRow label={t('Last Name')} value={provider.lastName || '-'} />
      <CompactTable.InfoRow label={t('Show on Calendar')} value={<ShowOnCalendarCell provider={provider} />} />
    </CompactTable.ExpandedRow>
  );
};

const styles = {
  rowHeader: (isArchived = false) => isArchived && css({ color: theme.font.colors.disabled }),
};
